import { defineStore } from 'pinia'
import { computed, ref, toValue, watch } from 'vue'

import { useApiStore } from '@/stores/api'
import { useEnvironmentStore } from '@/stores/environment'
import { useProgramStore } from '@/stores/program'

import type { ReleasePin } from '@policyfly/protobuf/pfrelease'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'

type MaybeStringRef = MaybeRefOrGetter<string | null | undefined>

export const useReleaseStore = defineStore('release', () => {
  const apiStore = useApiStore()
  const environmentStore = useEnvironmentStore()
  const programStore = useProgramStore()

  const pinsRef = ref<ReleasePin[]>([])
  const loading = ref(false)

  async function loadMatrix (): Promise<void> {
    const environmentId = environmentStore.currentEnvironment?.id
    if (!environmentId) {
      pinsRef.value = []
      return
    }
    loading.value = true
    try {
      await programStore.loadPrograms()
      const { pins } = await apiStore.getMatrix({ env: environmentId })
      pinsRef.value = pins
    } finally {
      loading.value = false
    }
  }

  function pinsForSlug (slug: MaybeStringRef): ComputedRef<ReleasePin[]> {
    return computed<ReleasePin[]>(() => {
      const currentSlug = toValue(slug)
      if (!currentSlug) return []
      return pinsRef.value.filter((pin) => pin.slug === currentSlug)
    })
  }

  async function pinVersion (slug: string, tag: string): Promise<void> {
    const env = environmentStore.currentEnvironment?.id
    if (!env) throw new Error('No current environment selected')
    const { pin } = await apiStore.pinVersion({
      slug,
      tag,
      env,
    })
    if (!pin) throw new Error('Failed to pin version.')
    const existingIndex = pinsRef.value.findIndex((p) => p.slug === slug)
    if (existingIndex !== -1) {
      pinsRef.value[existingIndex] = pin
    } else {
      pinsRef.value.push(pin)
    }
  }

  // Removes the pin only; the program follows the global tag again on the
  // next env-wide deploy, no deploy is triggered here.
  async function unpinVersion (slug: string): Promise<void> {
    const env = environmentStore.currentEnvironment?.id
    if (!env) throw new Error('No current environment selected')
    await apiStore.unpinVersion({ slug, env })
    pinsRef.value = pinsRef.value.filter((p) => !(p.slug === slug && p.env === env))
  }

  watch(() => environmentStore.currentEnvironment?.id, async () => {
    pinsRef.value = []
    await loadMatrix()
  }, { immediate: true })

  const pins = computed(() => pinsRef.value)
  const currentEnvironment = computed(() => environmentStore.currentEnvironment)

  return {
    pins,
    loading,
    currentEnvironment,
    loadMatrix,
    pinsForSlug,
    pinVersion,
    unpinVersion,
  }
})
